import React from "react";
import { Query } from "react-apollo";

/**
 * Component that configures Apollo Client for easy use in pagination.
 * 
 * In addition to returning the Apollo Client options in the render prop, we return the loadMoreRows function to fetch the next page.
 *
 * Props are:
 * - queryName: Name of the server side query that will be executed. This is used to extract the data from the server's response.
 * - query: GraphQL query document that must be executed by the server.
 * - variables: Object containing the variables used to execute the GraphQL query.
 * - onQueryUpdate: Callback function called initially when the query is set. This is useful to set the used query in a context so that it can be refetched when needed.
 * - onVariablesUpdate: Callback function called whenever the variables are updated by the paging function (with new offset). This is usefuly to set the variables in a context so that the query can be refetched when needed.
 */
class PagingQuery extends React.PureComponent {
  constructor(props) {
    super(props);

    // Default offset used by all paging, if none are specified in the variables
    this.defaultOffset = {
      startIndex: 0,
      stopIndex: 40
    };

    const { onQueryUpdate, onVariablesUpdate, query, variables } = props;

    // Use the callbacks with the initial values so things can be set in the context as needed.
    onQueryUpdate(query);
    onVariablesUpdate({...this.defaultOffset, ...variables});
  }

  render() {
    const { queryName, query, variables: defaultVariables, onVariablesUpdate, children, ...restProps } = this.props;

    return (
      <Query
        {...restProps}
        query={query}
        variables={{
          ...this.defaultOffset,
          ...defaultVariables
        }}
      >
        {({ loading, error, data, variables, fetchMore, refetch, networkStatus }) => {
          const loadMoreRows = ({ startIndex, stopIndex }) => {
            const offsetVariables = {
              startIndex: startIndex,
              stopIndex: stopIndex
            };

            // Update only the offset variables with the new position.
            onVariablesUpdate(offsetVariables);

            return fetchMore({
              variables: {
                ...variables,
                ...offsetVariables
              },

              //Fired when the store's cache needs to be updated
              updateQuery: (previousResult, { fetchMoreResult, variables }) => {
                const data = fetchMoreResult[queryName],
                  totalElements = data.totalElements,
                  newRows = data.content;

                //Copy the current query cache so that we can modify it.
                let newData = { ...previousResult };

                //Update the data with the new results (this is what will be stored in the Apollo cache)
                newData[queryName] = {
                  ...previousResult[queryName],
                  totalElements,
                  content: [...previousResult[queryName].content, ...newRows]
                };

                return newData;
              }
            });
          };

          return children({ loading, data, refetch, variables, networkStatus, loadMoreRows });
        }}
      </Query>
    );
  }
};

PagingQuery.defaultProps = {
  onQueryUpdate: () => {},
  onVariablesUpdate: () => {}
};

export default PagingQuery;
